import type { DateTime } from "luxon";
import type { Department } from "../../contexts/AuthProvider";
import { getWeekIndexOfSpecificDate, getWeekStart } from "../../utils/dateUtils";

// Single source of truth for which file extensions Onboarding Tools accepts on upload.
// Used by both Auto Prompt and Load sections; the route layer enforces the same set.
export const ACCEPTED_TYPES = [".csv", ".xls", ".xlsx", ".xlsm"] as const;

export const DROP_ZONE_HINT = "Drag & drop or click to choose a CSV/XLS/XLSX/XLSM file";

export function stripExtension(name: string, fallback: string): string {
	return name.replace(/\.[^/.]+$/, "") || fallback;
}

const WEEK_RANGE_FORMAT = "LLL d";

// The recurring CSV only ever says Week 1 or Week 2, and the loader writes `interval=2` to match, so
// the parity shown here is over two weeks — not `department.master_schedule_weeks`, which is 0 for a
// department with no master schedule configured.
const TEMPLATE_CYCLE_WEEKS = 2;

export const formatWeekRange = (weekStart: DateTime): string => {
	const weekEnd = weekStart.plus({ days: 6 });
	return `${weekStart.toFormat(WEEK_RANGE_FORMAT)} – ${weekEnd.toFormat(WEEK_RANGE_FORMAT)}`;
};

export type WeekInfo = {
	weekStart: DateTime;
	range: string;
	weekNumber: number; // 1-based: which week of the recurring cycle this calendar week maps to.
};

/**
 * "This week" and "next week" as the department's own weeks, with the cycle week each maps to.
 *
 * This dialog exists to tell the operator which parity they are about to load, so it has to agree
 * with the backend recurring loader — which anchors on `department.week_start_day`, gated by
 * ScheduleTemplateRuntimeWeekStart (DAT-831). Hence `weekStartDay` comes from the department column
 * via `resolveEffectiveWeekStartDay`, NOT from the per-user `viewStartDay` that Master Schedule
 * passes: that is a display preference and would make the dialog describe a week the loader never
 * uses. It previously went through `getDayWeekScheduleIndex`, which buckets on Luxon Monday weeks,
 * so the number shown disagreed with the backend on every day except Sunday.
 */
export function getThisAndNextWeek(department: Department, today: DateTime, weekStartDay: number): { current: WeekInfo; next: WeekInfo } {
	const currentStart = getWeekStart(today, weekStartDay);
	const nextStart = currentStart.plus({ weeks: 1 });
	const cycleWeekOf = (day: DateTime): number =>
		getWeekIndexOfSpecificDate({
			dateToCheck: day,
			masterScheduleWeeks: TEMPLATE_CYCLE_WEEKS,
			timezone: department.timezone,
			chosenDepartmentFirstSunday: department.first_sunday,
			weekStartDay,
		}) + 1;
	return {
		current: { weekStart: currentStart, range: formatWeekRange(currentStart), weekNumber: cycleWeekOf(currentStart) },
		next: { weekStart: nextStart, range: formatWeekRange(nextStart), weekNumber: cycleWeekOf(nextStart) },
	};
}

export function parseCsvPreview(buffer: ArrayBuffer): { headers: string[]; rows: string[][] } {
	const text = new TextDecoder("utf-8").decode(buffer);
	const lines = text.split("\n").filter((line) => line.trim().length > 0);
	if (lines.length === 0) return { headers: [], rows: [] };

	const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
	const rows = lines.slice(1).map((line) => line.split(",").map((c) => c.trim().replace(/^"|"$/g, "")));

	return { headers, rows };
}
